import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { combineLatest, Subject } from 'rxjs';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { QuestionnaireAction, QuestionnaireState } from './questionnaire.state';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { PricingPlanDto } from 'app/@types/dto/pricing-plan-dto.model';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { ResultsFetcherService } from '@services/results-fetcher/results-fetcher.service';
import { SolutionAction } from './solution.state';
import { QuestionnaireConfigAction } from './questionnaire.config.state';

export interface OrganizationModel extends BaseStateInterface {
    organization: OrganizationDto | undefined;
    questionnaires: QuestionnaireDto[] | undefined;
    availableOrganization: OrganizationDto[];
    availableYears: number[] | undefined;
    entities: OrganizationEntityDto[] | undefined;
    organizationPricingPlan: OrganizationPricingPlanDto | undefined;
    pricingPlan: PricingPlanDto | undefined;
}

export namespace OrganizationAction {
    export class Activate {
        public static type = '[Organization UI] Activate Questionnaire';

        constructor(public organizationId: string) {}
    }

    export class Deactivate {
        public static type = '[Organization UI] Deactivate Organization';
        constructor() {}
    }

    export class Refresh {
        public static type = '[Organization UI] Activate Questionnaire';

        constructor() {}
    }

    export class AddQuestionnairesFromPeriodAction {
        public static type = '[Organization UI] Add Questionnaires from a period';

        constructor(
            public organizationId: string,
            public year: number,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class CreateEntitiesAction {
        public static type = '[Organization UI] Add Questionnaires from an entity';

        constructor(
            public organizationId: string,
            public entity: OrganizationEntityDto,
            public callCompletion: Subject<OrganizationEntityDto | undefined>,
        ) {}
    }

    export class DeleteQuestionnairesFromPeriodAction {
        public static type = '[Organization UI] Soft delete Questionnaires from a period';

        constructor(
            public organizationId: string,
            public year: number,
            public activeQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class DeleteEntityAction {
        public static type =
            '[Organization UI] Soft delete Questionnaires from an entity and delete the entity itself.';

        constructor(
            public organizationId: string,
            public entityid: string,
            public activeQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class EditQuestionnairesPeriodAction {
        public static type = '[Organization UI] Change the period of all the questionnaires with a selected periods';

        constructor(
            public organizationId: string,
            public currentYear: number,
            public newYear: number,
            public currentQuestionnaire: QuestionnaireDto,
            public callCompletion: Subject<QuestionnaireDto[] | undefined>,
        ) {}
    }

    export class UpdateEntitieAction {
        public static type = '[Organization UI] Update the properties of an entity';

        constructor(
            public organizationId: string,
            public entityId: string,
            public entity: OrganizationEntityDto,
            public callCompletion: Subject<OrganizationEntityDto | undefined>,
        ) {}
    }
}

@State<OrganizationModel>({
    name: 'organization',
    defaults: {
        organization: undefined,
        availableOrganization: [],
        questionnaires: [],
        availableYears: [],
        entities: [],
        organizationPricingPlan: undefined,
        pricingPlan: undefined,
        isInit: false,
        isLoading: false,
        errors: [],
    },
})
@Injectable()
export class OrganizationState {
    private isInit: boolean = false;
    constructor(
        private questionnaireService: QuestionnaireHttpService,
        private store: Store,
        private adminHttpService: AdminHTTPService,
        private localStorageService: StorageService,
        private resultFetcherService: ResultsFetcherService,
    ) {}

    @Action(OrganizationAction.Activate)
    activate(ctx: StateContext<OrganizationModel>, action: OrganizationAction.Activate) {
        ctx.patchState({
            isLoading: true,
        });

        //Retreive the organization from the id.
        this.questionnaireService.getOrganization(action.organizationId).subscribe({
            next: (organization) => {
                if (organization.uid) {
                    this.questionnaireService.getEntities(organization.uid).subscribe({
                        next: (entities) => {
                            ctx.patchState({
                                organization: organization,
                                entities: entities,
                            });
                        },
                    });
                }
            },
        });

        //Retreive PricingPlan and orgPP to have access to the validity
        combineLatest([
            this.adminHttpService.getOrganizationPricingPlan(action.organizationId),
            this.adminHttpService.getPricingPlan(action.organizationId),
        ]).subscribe({
            next: ([orgPP, PP]) => {
                ctx.patchState({
                    organizationPricingPlan: orgPP,
                    pricingPlan: PP,
                });
            },
        });

        //Retreive the questionnaires
        this.questionnaireService.getAll(action.organizationId).subscribe((questionnairesQuestionInstances) => {
            let questionnaires = questionnairesQuestionInstances.map(({ questionnaire }) => questionnaire);
            const possibleYears = Array.from({ length: 2050 - 2018 + 1 }, (_, index) => 2018 + index);
            const nonDeletedQuestionnaires = questionnaires.filter((quest) => !quest.deleted);
            const existingYears = nonDeletedQuestionnaires.map((quest) => quest.firstYearMeasured);
            const availableYears = possibleYears.filter((year) => !existingYears.includes(year));
            ctx.patchState({
                questionnaires: questionnaires,
                availableYears: availableYears,
            });
        });
    }

    @Action(OrganizationAction.AddQuestionnairesFromPeriodAction)
    CreateQuestionnairesFromPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.AddQuestionnairesFromPeriodAction,
    ) {
        this.questionnaireService.createQuestionnaireFromPeriod(action.organizationId, action.year).subscribe({
            next: (createdQuestionnaires) => {
                let currentQuestionnaires = ctx.getState().questionnaires || [];
                currentQuestionnaires = currentQuestionnaires.filter(
                    (quest) => quest.firstYearMeasured !== action.year,
                ); // If the year was undelete we remove the questionnaires before adding new ones. (Cleaning)
                const currentAvailableYears = ctx.getState().availableYears || [];
                ctx.patchState({
                    questionnaires: [...currentQuestionnaires, ...createdQuestionnaires],
                    availableYears: currentAvailableYears.filter((year) => year !== action.year),
                });
                action.callCompletion.next(createdQuestionnaires);
            },
            error: (error) => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.CreateEntitiesAction)
    CreateEntities(ctx: StateContext<OrganizationModel>, action: OrganizationAction.CreateEntitiesAction) {
        this.questionnaireService.createEntity(action.organizationId, action.entity).subscribe({
            next: (createdEntity) => {
                this.questionnaireService.getAll(action.organizationId).subscribe((questionnairesQuestionInstances) => {
                    const questionnaires = questionnairesQuestionInstances.map(({ questionnaire }) => questionnaire);
                    const currentEntities = ctx.getState().entities || [];
                    ctx.patchState({
                        questionnaires: questionnaires,
                        entities: [...currentEntities, createdEntity],
                    });
                    action.callCompletion.next(createdEntity);
                });
            },
            error: (error) => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.DeleteQuestionnairesFromPeriodAction)
    DeleteQuestionnairesFromPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.DeleteQuestionnairesFromPeriodAction,
    ) {
        this.questionnaireService.deleteQuestionnaireFromPeriod(action.organizationId, action.year).subscribe({
            next: (deletedQuestionnaires) => {
                const currentQuestionnaires = ctx.getState().questionnaires || [];
                let updatedQuestionnaires = currentQuestionnaires.filter(
                    (quest) => quest.firstYearMeasured !== action.year,
                );
                updatedQuestionnaires = [...updatedQuestionnaires, ...deletedQuestionnaires]; //Even if SD, they should still be in questionnaires state.
                const currentAvailableYears = ctx.getState().availableYears || [];
                currentAvailableYears.push(action.year);
                ctx.patchState({
                    questionnaires: updatedQuestionnaires,
                    availableYears: currentAvailableYears.sort((a, b) => a - b),
                });
                action.callCompletion.next(deletedQuestionnaires);
                const sortedquestionaires = updatedQuestionnaires
                    .filter((quest) => !quest.deleted && quest.entityId === action.activeQuestionnaire.entityId)
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
                this.store.dispatch(
                    new QuestionnaireAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].firstYearMeasured,
                        sortedquestionaires[0].entityId,
                    ),
                );
                this.store.dispatch(
                    new QuestionnaireConfigAction.Activate(action.organizationId, sortedquestionaires[0].entityId),
                );
            },
            error: (error) => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.DeleteEntityAction)
    DeleteEntity(ctx: StateContext<OrganizationModel>, action: OrganizationAction.DeleteEntityAction) {
        this.questionnaireService.deleteEntity(action.organizationId, action.entityid).subscribe({
            next: (deletedQuestionnaires) => {
                const currentQuestionnaires = ctx.getState().questionnaires || [];
                let updatedQuestionnaires = currentQuestionnaires.filter((quest) => quest.entityId !== action.entityid);
                updatedQuestionnaires = [...updatedQuestionnaires, ...deletedQuestionnaires]; //Even if SD, they should still be in questionnaires state.
                const updatedEntites = ctx.getState().entities?.filter((entity) => entity.id !== action.entityid);
                ctx.patchState({
                    questionnaires: updatedQuestionnaires,
                    entities: updatedEntites,
                });
                action.callCompletion.next(deletedQuestionnaires);
                const sortedquestionaires = updatedQuestionnaires
                    .filter(
                        (quest) =>
                            !quest.deleted && quest.firstYearMeasured === action.activeQuestionnaire.firstYearMeasured,
                    )
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
                this.store.dispatch(
                    new QuestionnaireAction.Activate(
                        action.organizationId,
                        sortedquestionaires[0].firstYearMeasured,
                        sortedquestionaires[0].entityId,
                    ),
                );
                this.store.dispatch(
                    new QuestionnaireConfigAction.Activate(action.organizationId, sortedquestionaires[0].entityId),
                );
            },
            error: (error) => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.UpdateEntitieAction)
    UpdateEntitie(ctx: StateContext<OrganizationModel>, action: OrganizationAction.UpdateEntitieAction) {
        this.questionnaireService.updateEntity(action.organizationId, action.entityId, action.entity).subscribe({
            next: (modifiedEntity) => {
                let currentEntities = ctx.getState().entities || [];
                //Remove the entity
                currentEntities = currentEntities.filter((entity) => entity.id !== action.entityId);
                const updatedEntities = [...currentEntities, modifiedEntity];
                ctx.patchState({
                    entities: updatedEntities,
                });
                this.resultFetcherService.setInvalid();
                this.store.dispatch(new SolutionAction.Invalidate());
                const currentQuestionnaire = this.store.selectSnapshot(QuestionnaireState.getQuestionnaire);
                //We only need to update the config if the modified entity is the one of our active questionnaire.
                if (currentQuestionnaire && currentQuestionnaire.entityId === action.entityId)
                    this.store.dispatch(new QuestionnaireConfigAction.Activate(action.organizationId, action.entityId));
                action.callCompletion.next(modifiedEntity);
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(OrganizationAction.EditQuestionnairesPeriodAction)
    EditQuestionnairesPeriod(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.EditQuestionnairesPeriodAction,
    ) {
        this.questionnaireService
            .editQuestionnairesPeriod(action.organizationId, action.currentYear, action.newYear)
            .subscribe({
                next: (modifiedQuestionnaires) => {
                    let currentQuestionnaires = ctx.getState().questionnaires || [];
                    currentQuestionnaires = currentQuestionnaires.filter(
                        (quest) =>
                            quest.firstYearMeasured !== action.currentYear &&
                            quest.firstYearMeasured !== action.newYear,
                    );
                    const currentAvailableYears = ctx.getState().availableYears || [];
                    currentAvailableYears.push(action.currentYear);
                    ctx.patchState({
                        questionnaires: [...currentQuestionnaires, ...modifiedQuestionnaires],
                        availableYears: currentAvailableYears
                            .filter((year) => year !== action.newYear)
                            .sort((a, b) => a - b),
                        entities: ctx.getState().entities,
                    });
                    this.store.dispatch(
                        new QuestionnaireAction.Activate(
                            action.organizationId,
                            action.newYear,
                            action.currentQuestionnaire.entityId,
                        ),
                    );
                    this.resultFetcherService.setInvalid();
                    this.store.dispatch(new SolutionAction.Invalidate());
                    action.callCompletion.next(modifiedQuestionnaires);
                },
                error: (error) => {
                    action.callCompletion.error(undefined);
                },
            });
    }

    @Action(OrganizationAction.Deactivate) deactivate(
        ctx: StateContext<OrganizationModel>,
        action: OrganizationAction.Deactivate,
    ) {
        ctx.patchState({
            organization: undefined,
            entities: undefined,
            organizationPricingPlan: undefined,
            pricingPlan: undefined,
            questionnaires: undefined,
            availableYears: undefined,
        });
    }

    @Selector([OrganizationState])
    static getOrganization(state: OrganizationModel) {
        return state.organization;
    }

    @Selector([OrganizationState])
    static getQuestionnaires(state: OrganizationModel) {
        return state.questionnaires;
    }

    @Selector([OrganizationState])
    static getAvailableYears(state: OrganizationModel) {
        return state.availableYears;
    }

    @Selector([OrganizationState])
    static getEntities(state: OrganizationModel) {
        return state.entities;
    }

    @Selector([OrganizationState])
    static getOrganizationPricingPlan(state: OrganizationModel) {
        return state.organizationPricingPlan;
    }

    @Selector([OrganizationState])
    static getPricingPlan(state: OrganizationModel) {
        return state.pricingPlan;
    }
}
